import { Base } from "ui/Scripts/source/framework/base";
import { allPartnersCompanyGroupCode } from "ui/Scripts/source/models/consts";
import { CompanyGroupType, EnumHelper, GroupPermissionLevel, GroupType, UserGroupType } from "ui/Scripts/source/models/enums";
import { IUnitPermissionUserGroup } from "../userGroup/unitPermissionUserGroup";
import { EffectiveUnitNodeCompanyIds } from "./effectiveUnitNodeCompanyIds";
import { UnitNodeGetChildPermissionsResult } from "./unitNodeGetChildPermissionsResult";
import { IUnitNodePermission, UnitNodePermission } from "./unitNodePermission";
import { IUnitNodePermissionCompanyGroupPermission, UnitNodePermissionCompanyGroupPermission } from "./unitNodePermissionCompanyGroupPermission";
import { IUnitNodeSave, UnitNodeSave } from "./unitNodeSave";
import { UnitNodeSavePermission } from "./unitNodeSavePermission";

export interface IUnitNode {
    systemPermissionLevel: number;
    maxPermissionLevel: number;
    maxPermissionLevelA: number;
    maxPermissionLevelB: number;
    maxPermissionLevelC: number;
    id: string;
    name: string;
    explicitPermissions: UnitNodePermission[];
    children: UnitNode[];
    partnerCompanyIds: string[];
    parent: UnitNode;
    permission: IUnitNodePermissionCompanyGroupPermission;

    getIsRoot(): boolean;
    getIsDisabled(): boolean;
    hasChildren(): boolean;
    hasSiblings(): boolean;
    getUnitNodeById(id: string): UnitNode;
    hasExplicitPermission(companyGroupCode: string, userGroupCode: string): boolean;
    getEffectiveNonExplicitPermission(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup): UnitNodePermissionCompanyGroupPermission;
    getNodePermissions(companyGroupCode: string, userGroupCode: string, list: { [id: string]: IUnitNodePermission; }): { [id: string]: IUnitNodePermission; };
    setGroupPermissions(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup, permissions: { [id: string]: IUnitNodePermission; });
    getEffectiveUnitNodeCompanyIds(userGroup: IUnitPermissionUserGroup): EffectiveUnitNodeCompanyIds;
    getJsonUnitNodeSaves(list: IUnitNodeSave[], groupCode: string, groupType: number): IUnitNodeSave[];
}

export class UnitNode implements IUnitNode {
    systemPermissionLevel: number;
    maxPermissionLevel: number;
    maxPermissionLevelA: number;
    maxPermissionLevelB: number;
    maxPermissionLevelC: number;
    id: string;
    name: string;
    explicitPermissions: UnitNodePermission[];
    children: UnitNode[];
    partnerCompanyIds: string[];
    parent: UnitNode;
    permission: UnitNodePermissionCompanyGroupPermission;

    constructor();
    constructor(obj: IUnitNode);
    constructor(obj?: any) {
        this.systemPermissionLevel = obj && obj.systemPermissionLevel || 0;
        this.maxPermissionLevel = obj && obj.maxPermissionLevel || 0;
        this.maxPermissionLevelA = obj && obj.maxPermissionLevelA || 0;
        this.maxPermissionLevelB = obj && obj.maxPermissionLevelB || 0;
        this.maxPermissionLevelC = obj && obj.maxPermissionLevelC || 0;
        this.id = obj && obj.id || "";
        this.name = obj && obj.name || "";
        this.explicitPermissions = null;
        if (obj && obj.explicitPermissions) {
            this.explicitPermissions = Base.getTypedArray(obj ? obj.explicitPermissions : null, UnitNodePermission);
        }
        this.children = [];
        this.parent = null;
        if (obj && obj.children) {
            this.children = Base.getTypedArray(obj.children, UnitNode);
            for (const child of this.children) {
                child.parent = this;
            }
        }
        this.partnerCompanyIds = obj && obj.partnerCompanyIds || [];
        this.permission = obj && obj.permission || new UnitNodePermissionCompanyGroupPermission();
    }

    getIsRoot(): boolean {
        return !this.parent;
    }

    getRoot(): UnitNode {
        return this.getIsRoot() ? this : this.parent.getRoot();
    }

    getSiblings(): UnitNode[] {
        if (this.getIsRoot()) return [];
        return this.parent.children.filter(i => i.id !== this.id);
    }

    getPathNodes(): UnitNode[] {
        if (this.getIsRoot()) return [this];
        const result: UnitNode[] = this.parent.getPathNodes();
        result.push(this);
        return result;
    }

    getTotalMaxPermission(): number {
        return UnitNodePermission.getTotalPermission(this.maxPermissionLevel, this.maxPermissionLevelA, this.maxPermissionLevelB, this.maxPermissionLevelC);
    }

    getIsDisabled(): boolean {
        const totalMaxPermission = this.getTotalMaxPermission();
        const totalPermission = this.permission.getTotalUserGroupPermission();
        return totalMaxPermission === 0 || totalMaxPermission < totalPermission;
    }

    hasChildren(): boolean {
        return this.children.length > 0;
    }

    hasSiblings(): boolean {
        if (this.getIsRoot()) return false;
        return this.parent.children.length > 1;
    }

    getUnitNodeById(id: string): UnitNode {
        if (!id) return null;
        if (this.id.equalIgnoreCase(id)) return this;
        for (const child of this.children) {
            const childResult = child.getUnitNodeById(id);
            if (childResult) return childResult;
        }
        return null;
    }

    removeGroupPermission(groupType: number, groupCode: string) {
        if (this.explicitPermissions === null || this.explicitPermissions.length === 0 || this.getIsDisabled()) return;
        const index = this.explicitPermissions.findIndex(i => i.type === groupType && i.code.equalIgnoreCase(groupCode));
        if (index >= 0) {
            this.explicitPermissions.splice(index, 1);
        }
    }

    removeGroupPermissions(groupType: number, groupCode: string) {
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0 && !this.getIsDisabled()) {
            const index = this.explicitPermissions.findIndex(i => i.type === groupType && i.code.equalIgnoreCase(groupCode));
            if (index >= 0) {
                this.explicitPermissions.splice(index, 1);
            }
        }
        if (this.children.length < 1) return;
        for (const child of this.children) {
            child.removeGroupPermissions(groupType, groupCode);
        }
    }

    setGroupPermission(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup, permission: number, additionalPermissionA: number, additionalPermissionB: number, additionalPermissionC: number, clearSubFolders: boolean) {
        if (this.getIsDisabled()) return;
        permission = Math.min(permission, GroupPermissionLevel.Write);
        additionalPermissionA = Math.min(additionalPermissionA, 1);
        additionalPermissionB = Math.min(additionalPermissionB, 1);
        additionalPermissionC = Math.min(additionalPermissionC, 1);
        //UserGroup / CompanyGroup selection
        let groupType: number, groupSubType: number, groupCode: string;
        if (userGroup) {
            groupType = GroupType.UserGroup;
            groupCode = userGroup.code;
            groupSubType = userGroup.type;
        } else {
            groupType = GroupType.CompanyGroup;
            groupCode = companyGroup.code;
            groupSubType = companyGroup.type;
        }
        //Clear subfolder permissions
        if (clearSubFolders && this.hasChildren()) {
            for (const child of this.children) {
                child.removeGroupPermissions(groupType, groupCode);
            }
        }
        //Add or Modify permission
        let currentPermission: UnitNodePermission = null;
        if (this.explicitPermissions === null) {
            this.explicitPermissions = [];
        } else {
            currentPermission = this.explicitPermissions.find(j => j.type === groupType && j.code.equalIgnoreCase(groupCode));
        }
        if (currentPermission === null || currentPermission === undefined) {
            currentPermission = new UnitNodePermission();
            currentPermission.type = groupType;
            currentPermission.groupType = groupSubType;
            currentPermission.code = groupCode;
            this.explicitPermissions.push(currentPermission);
        }
        currentPermission.level = permission;
        currentPermission.levelA = additionalPermissionA;
        currentPermission.levelB = additionalPermissionB;
        currentPermission.levelC = additionalPermissionC;
    }

    setGroupPermissions(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup, permissions: { [id: string]: IUnitNodePermission; }) {
        //Remove old permission
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0 && !this.getIsDisabled()) {
            const index = userGroup
                ? this.explicitPermissions.findIndex(i => i.code.equalIgnoreCase(userGroup.code) && i.type === GroupType.UserGroup)
                : this.explicitPermissions.findIndex(i => i.code.equalIgnoreCase(companyGroup.code) && i.type === GroupType.CompanyGroup);
            if (index >= 0) {
                this.explicitPermissions.splice(index, 1);
            }
        }
        //Add new permission if exist
        /* eslint-disable no-prototype-builtins */
        if (permissions.hasOwnProperty(this.id)) {
            this.setGroupPermission(companyGroup, userGroup, permissions[this.id].level, permissions[this.id].levelA, permissions[this.id].levelB, permissions[this.id].levelC, false);
        }
        /* eslint-enable no-prototype-builtins */
        if (this.children.length < 1) return;
        for (const child of this.children) {
            child.setGroupPermissions(companyGroup, userGroup, permissions);
        }
    }

    getPermissionsBase(explicitPermissions: UnitNodePermission[]): UnitNodePermission[] {
        if (this.getIsRoot()) {
            return explicitPermissions;
        }
        let result = this.parent.getPermissions();
        if (explicitPermissions != null) {
            result = result.slice(0);
            let index: number;
            //'All'-permissions replaces all same type inherited permissions, normal permission replaces only same code inherited permission if not in ROOT
            const allPermissions = explicitPermissions.filter(i => !i.code);
            for (const allPermission of allPermissions) {
                const parentPermissions = result.filter(j => j.type === allPermission.type);
                for (const parentPermission of parentPermissions) {
                    index = result.findIndex(k => k.type === parentPermission.type);
                    if (index < 0) continue;
                    result.splice(index, 1);
                }
            }
            //Group permissions
            for (const explicitPermission of explicitPermissions) {
                //Remove inherited permission if not in ROOT
                index = result.findIndex(k => k.type === explicitPermission.type && k.code.equalIgnoreCase(explicitPermission.code));
                if (index > -1) {
                    result.splice(index, 1);
                }
                //Add group permission
                result.push(explicitPermission);
            }
        }
        return result;
    }

    getPermissions(): UnitNodePermission[] {
        return this.getPermissionsBase(this.explicitPermissions);
    }

    getEffectivePermissionBase(permissions: UnitNodePermission[], companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup): UnitNodePermissionCompanyGroupPermission {
        const result = new UnitNodePermissionCompanyGroupPermission();
        if (!this.getIsDisabled()) {
            result.setUserGroupPermissions(this.maxPermissionLevel, this.maxPermissionLevelA, this.maxPermissionLevelB, this.maxPermissionLevelC);
            result.setCompanyGroupPermissions(this.maxPermissionLevel, this.maxPermissionLevelA, this.maxPermissionLevelB, this.maxPermissionLevelC);
        } else {
            result.setUserGroupPermissions(this.systemPermissionLevel, 1, 1, 1);
            result.setCompanyGroupPermissions(this.systemPermissionLevel, 1, 1, 1);
        }
        result.isExplicitPermission = false;
        //#1 Company group permissions are relevant only for users that do not belong to resource owning company
        //- Company of user that does not belong to resource owning company must always belong to normal company group in order to access folders
        //- Highest permissions considering all groups that user belongs to
        //- 'All group' permissions are used only if normal group permission does not exist in the same level
        if (companyGroup) {
            let companyGroupPermission: UnitNodePermission = null;
            let allCompanyGroupPermission: UnitNodePermission = null;
            let allPartnersCompanyGroupPermission: UnitNodePermission = null;
            let oneCompanyGroupPermission: UnitNodePermission = null;
            const relevantCompanyGroupPermissions = permissions.filter(k => EnumHelper.isEqual(k.type, GroupType.CompanyGroup));
            const companyGroupCodes = companyGroup.groupCodes.slice(0);
            companyGroupCodes.push(companyGroup.code);
            for (const permission of relevantCompanyGroupPermissions) {
                if (!permission.code) { //'All companies' group
                    allCompanyGroupPermission = permission;
                } else if (permission.code.equalIgnoreCase(allPartnersCompanyGroupCode)) { //'All partners' group
                    allPartnersCompanyGroupPermission = permission;
                } else {
                    for (const companyGroupCode of companyGroupCodes) {
                        if (!permission.code.equalIgnoreCase(companyGroupCode)) continue;
                        if (EnumHelper.isEqual(permission.groupType, CompanyGroupType.Fixed)) { //One company company group
                            oneCompanyGroupPermission = permission;
                        } else { //Normal company groups
                            if (companyGroupPermission === null || permission.level > companyGroupPermission.level) {
                                companyGroupPermission = permission;
                            } else if (permission.level === companyGroupPermission.level) {
                                companyGroupPermission = companyGroupPermission.getAdditionalPermissionUnion(permission);
                            }
                        }
                        break;
                    }
                }
            }
            const resultTotal = UnitNodePermission.getTotalPermission(result.permission, result.additionalPermissionA, result.additionalPermissionB, result.additionalPermissionC);
            if (oneCompanyGroupPermission !== null) {
                if (oneCompanyGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(oneCompanyGroupPermission);
                }
            } else if (companyGroupPermission !== null) {
                if (companyGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(companyGroupPermission);
                }
            } else if (allPartnersCompanyGroupPermission !== null) {
                if (allPartnersCompanyGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(allPartnersCompanyGroupPermission);
                }
            } else if (allCompanyGroupPermission !== null) {
                if (allCompanyGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(allCompanyGroupPermission);
                }
            }
        }
        result.setCompanyGroupPermissions(result.permission, result.additionalPermissionA, result.additionalPermissionB, result.additionalPermissionC);
        //console.log(this.name, userGroup.code, result.permission);
        if (EnumHelper.isEqual(result.permission, GroupPermissionLevel.None)) return result;
        //#2 User group permissions are relevant for all users
        //- Highest permissions considering all groups that user belongs to
        //- 'All group' permissions are used only if normal group permission does not exist
        if (userGroup) {
            let userGroupPermission: UnitNodePermission = null;
            let allUserGroupPermission: UnitNodePermission = null;
            let oneUserGroupPermission: UnitNodePermission = null;
            const relevantUserGroupPermissions = permissions.filter(k => EnumHelper.isEqual(k.type, GroupType.UserGroup));
            const userGroupCodes = userGroup.groupCodes.slice(0);
            userGroupCodes.push(userGroup.code);
            for (const permission of relevantUserGroupPermissions) {
                if (!permission.code) { //'All users' group
                    allUserGroupPermission = permission;
                } else {
                    for (const userGroupCode of userGroupCodes) {
                        if (!permission.code.equalIgnoreCase(userGroupCode)) continue;
                        if (EnumHelper.isEqual(permission.groupType, UserGroupType.Fixed)) { //One user user group
                            oneUserGroupPermission = permission;
                        } else { //Normal user groups
                            if (userGroupPermission === null || permission.level > userGroupPermission.level) {
                                userGroupPermission = permission;
                            } else if (permission.level === userGroupPermission.level) {
                                userGroupPermission = userGroupPermission.getAdditionalPermissionUnion(permission);
                            }
                        }
                        break;
                    }
                }
            }
            const resultTotal = UnitNodePermission.getTotalPermission(result.permission, result.additionalPermissionA, result.additionalPermissionB, result.additionalPermissionC);
            if (oneUserGroupPermission !== null) {
                if (oneUserGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(oneUserGroupPermission);
                }
            } else if (userGroupPermission !== null) {
                if (userGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(userGroupPermission);
                }
            } else if (allUserGroupPermission !== null) {
                if (allUserGroupPermission.getLevelTotal() < resultTotal) {
                    result.setUserGroupPermissionsFromUnitNodePermission(allUserGroupPermission);
                }
            }
        }
        return result;
    }

    getEffectivePermission(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup): UnitNodePermissionCompanyGroupPermission {
        const result = this.getEffectivePermissionBase(this.getPermissions(), companyGroup, userGroup);
        result.isExplicitPermission = this.hasExplicitPermission(companyGroup ? companyGroup.code : null, userGroup ? userGroup.code : null);
        return result;
    }

    getEffectiveNonExplicitPermission(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup): UnitNodePermissionCompanyGroupPermission {
        const explicitPermissions = this.explicitPermissions != null ? this.explicitPermissions.slice(0) : null;
        if (explicitPermissions) {
            const index = userGroup
                ? explicitPermissions.findIndex(i => i.code.equalIgnoreCase(userGroup.code) && EnumHelper.isEqual(i.type, GroupType.UserGroup))
                : explicitPermissions.findIndex(i => i.code.equalIgnoreCase(companyGroup.code) && EnumHelper.isEqual(i.type, GroupType.CompanyGroup));
            if (index >= 0) {
                explicitPermissions.splice(index, 1);
            }
        }
        const result = this.getEffectivePermissionBase(this.getPermissionsBase(explicitPermissions), companyGroup, userGroup);
        result.isExplicitPermission = false;
        return result;
    }

    getChildPermissions(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup): UnitNodeGetChildPermissionsResult {
        const result = new UnitNodeGetChildPermissionsResult();
        for (const child of this.children) {
            const permission = child.getEffectivePermission(companyGroup, userGroup);
            result.permissions[child.id] = permission;
        }
        return result;
    }

    getExplicitUnitNodePermission(companyGroupCode: string, userGroupCode: string): UnitNodePermission {
        let result: UnitNodePermission = null;
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
            result = !Base.isNullOrUndefined(userGroupCode)
                ? this.explicitPermissions.find(i => i.code.equalIgnoreCase(userGroupCode) && EnumHelper.isEqual(i.type, GroupType.UserGroup))
                : this.explicitPermissions.find(i => i.code.equalIgnoreCase(companyGroupCode) && EnumHelper.isEqual(i.type, GroupType.CompanyGroup));
        }
        return result;
    }

    getExplicitPermission(companyGroupCode: string, userGroupCode: string): number {
        const permission = this.getExplicitUnitNodePermission(companyGroupCode, userGroupCode);
        return permission ? permission.level : -1;
    }

    hasExplicitPermission(companyGroupCode: string, userGroupCode: string): boolean {
        return this.getExplicitPermission(companyGroupCode, userGroupCode) > -0.5;
    }

    setEffectivePermissions(companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup, preCalculatedPermission: UnitNodePermissionCompanyGroupPermission = null) {
        const permission = preCalculatedPermission ?? this.getEffectivePermission(companyGroup, userGroup);
        this.permission = permission;
        //console.log(this.name + ": " + permission.permission, userGroup);
        const permissionsOfChildren = this.getChildPermissions(companyGroup, userGroup);
        for (const child of this.children) {
            child.setEffectivePermissions(companyGroup, userGroup, permissionsOfChildren.permissions[child.id]);
        }
    }

    getAsList(list: UnitNode[]): UnitNode[] {
        list.push(this);
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getAsList(list);
        }
        return list;
    }
    
    getJsonUnitNodeSaves(list: IUnitNodeSave[], groupCode: string = null, groupType: number = null): IUnitNodeSave[] {
        if (!this.getIsDisabled()) {
            let permissions: UnitNodePermission[] = [];
            if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
                if (!groupCode || !groupType) {
                    permissions = this.explicitPermissions.slice(0);
                } else {
                    permissions = this.explicitPermissions.filter(j => j.type === groupType && j.code.equalIgnoreCase(groupCode));
                }
            }
            let saveUnitNode = list.find(j => j.id === this.id);
            if (!saveUnitNode) {
                saveUnitNode = new UnitNodeSave();
                saveUnitNode.id = this.id;
                list.push(saveUnitNode);
            }
            for (const permission of permissions) {
                if (saveUnitNode.permissions.findIndex(j => j.type === permission.type && j.code.equalIgnoreCase(permission.code)) >= 0) continue;
                const savePermission = new UnitNodeSavePermission();
                savePermission.type = permission.type;
                savePermission.code = permission.code;
                savePermission.level = permission.level;
                savePermission.levelA = permission.levelA;
                savePermission.levelB = permission.levelB;
                savePermission.levelC = permission.levelC;
                savePermission.companyIds = permission.companyIds.slice(0);
                saveUnitNode.permissions.push(savePermission);
            }
        }
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getJsonUnitNodeSaves(list, groupCode, groupType);
        }
        return list;
    }

    getNodePermissions(companyGroupCode: string, userGroupCode: string, list: { [id: string]: IUnitNodePermission; }): { [id: string]: IUnitNodePermission; } {
        const permission = this.getExplicitUnitNodePermission(companyGroupCode, userGroupCode);
        if (permission) {
            list[this.id] = new UnitNodePermission(permission);
        }
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getNodePermissions(companyGroupCode, userGroupCode, list);
        }
        return list;
    }

    getAllExplicitUserGroupCodesInUnitNodes(unitNodes: UnitNode[]): string[] {
        const result: string[] = [];
        for (const unitNode of unitNodes) {
            if (unitNode.explicitPermissions === null || unitNode.explicitPermissions.length < 1) continue;
            for (const explicitPermission of unitNode.explicitPermissions) {
                if (!EnumHelper.isEqual(explicitPermission.type, GroupType.UserGroup)) continue;
                if (result.indexOf(explicitPermission.code) >= 0) continue;
                result.push(explicitPermission.code);
            }
        }
        return result;
    }

    setEffectivePermissionsToPathNodes(pathNodes: UnitNode[], userGroup: IUnitPermissionUserGroup) {
        for (const pathNode of pathNodes) {
            pathNode.permission = pathNode.getEffectivePermission(null, userGroup);
        }
    }

    getMinPermissionUserGroups(userGroups: IUnitPermissionUserGroup[], minPermission: number, minAdditionalPermissionA: number, minAdditionalPermissionB: number, minAdditionalPermissionC: number, expandFixed: boolean): string[] {
        const minPermissionTotal = UnitNodePermission.getTotalPermission(minPermission, minAdditionalPermissionA, minAdditionalPermissionB, minAdditionalPermissionC);
        const result: string[] = [];
        const pathNodes = this.getPathNodes();
        const userGroupCodes = this.getAllExplicitUserGroupCodesInUnitNodes(pathNodes);
        for (const userGroupCode of userGroupCodes) {
            const userGroup = userGroups.find(j => j.code.equalIgnoreCase(userGroupCode));
            if (!userGroup) continue;
            this.setEffectivePermissionsToPathNodes(pathNodes, userGroup);
            if (this.permission.getTotalUserGroupPermission() < minPermissionTotal) continue;
            result.push(userGroupCode);
        }
        //Check if fixed user group has right base on normal user group
        if (expandFixed) {
            for (const userGroup of userGroups) {
                if (!EnumHelper.isEqual(userGroup.type, UserGroupType.Fixed)) continue;
                if (result.indexOf(userGroup.code) >= 0) continue;
                for (const userGroupCode of userGroup.groupCodes) {
                    if (result.indexOf(userGroupCode) < 0) continue;
                    result.push(userGroup.code);
                    break;
                }
            }
        }
        return result;
    }

    getMinPermissionUserGroupCodes(userGroups: IUnitPermissionUserGroup[], unitId: string, minPermission: number, minAdditionalPermissionA: number, minAdditionalPermissionB: number, minAdditionalPermissionC: number, expandFixed: boolean): string[] {
        const cloneUnitNode = new UnitNode(this);
        const resultUnitNode = cloneUnitNode.getUnitNodeById(unitId);
        if (!resultUnitNode) return [];
        return resultUnitNode.getMinPermissionUserGroups(userGroups, minPermission, minAdditionalPermissionA, minAdditionalPermissionB, minAdditionalPermissionC, expandFixed);
    }

    getMinPermissionUserGroupCodesInTree(userGroups: IUnitPermissionUserGroup[], minPermission: number, minAdditionalPermissionA: number, minAdditionalPermissionB: number, minAdditionalPermissionC: number, expandFixed: boolean): string[] {
        const minPermissionTotal = UnitNodePermission.getTotalPermission(minPermission, minAdditionalPermissionA, minAdditionalPermissionB, minAdditionalPermissionC);
        const result: string[] = [];
        const cloneUnitNode = new UnitNode(this);
        const unitNodes = cloneUnitNode.getAsList([]);
        const userGroupCodes = this.getAllExplicitUserGroupCodesInUnitNodes(unitNodes);
        for (const userGroupCode of userGroupCodes) {
            const userGroup = userGroups.find(j => j.code.equalIgnoreCase(userGroupCode));
            if (!userGroup) continue;
            cloneUnitNode.setEffectivePermissions(null, userGroup, null);
            for (const unitNode of unitNodes) {
                if (unitNode.permission.getTotalUserGroupPermission() < minPermissionTotal) continue;
                result.push(userGroupCode);
                break;
            }
        }
        //Check if fixed user group has right base on normal user group
        if (expandFixed) {
            for (const userGroup of userGroups) {
                if (!EnumHelper.isEqual(userGroup.type, UserGroupType.Fixed)) continue;
                if (result.indexOf(userGroup.code) >= 0) continue;
                for (const userGroupCode of userGroup.groupCodes) {
                    if (result.indexOf(userGroupCode) < 0) continue;
                    result.push(userGroup.code);
                    break;
                }
            }
        }
        return result;
    }

    getTreeHasPartnerCompanies(): boolean {
        if (this.partnerCompanyIds.length > 0) return true;
        for (const child of this.children) {
            const childResult = child.getTreeHasPartnerCompanies();
            if (childResult) return true;
        }
        return false;
    }

    addPartnerCompanyToUnit(unitId: string, partnerCompanyId: string) {
        if (!unitId || !partnerCompanyId) return;
        const node = this.getUnitNodeById(unitId);
        if (!node) return;
        if (node.partnerCompanyIds.indexOf(partnerCompanyId) > -1) return;
        node.partnerCompanyIds.push(partnerCompanyId);
    }

    removePartnerCompanyFromTree(unitId: string, partnerCompanyId: string) {
        if (!unitId || !partnerCompanyId) return;
        const node = this.getUnitNodeById(unitId);
        if (!node) return;
        const index = node.partnerCompanyIds.indexOf(partnerCompanyId);
        if (index < 0) return;
        node.partnerCompanyIds.splice(index, 1);
        if (!node.explicitPermissions || node.explicitPermissions.length < 1) return;
        for (const permission of node.explicitPermissions) {
            const index2 = permission.companyIds.indexOf(partnerCompanyId);
            if (index2 < 0) continue;
            permission.companyIds.splice(index2, 1);
        }
    }

    getPartnerCompanyIdsFromTree(unitId: string): string[] {
        if (!unitId) return [];
        const node = this.getUnitNodeById(unitId);
        if (!node) return [];
        return node.partnerCompanyIds.slice(0);
    }

    //Permission CompanyIds
    //#region Permission CompanyIds
    setGroupPermissionCompanyIds(userGroup: IUnitPermissionUserGroup, companyIds: string[]) {
        if (this.getIsDisabled() || !companyIds) return;
        //UserGroup / CompanyGroup selection
        if (userGroup) {
            const groupType = GroupType.UserGroup;
            const groupCode = userGroup.code;
            //Set companyIds
            const currentPermission = this.explicitPermissions.find(j => EnumHelper.isEqual(j.type, groupType) && j.code.equalIgnoreCase(groupCode));
            if (!currentPermission) return;
            currentPermission.companyIds = companyIds.slice(0);
        }
    }

    //getGroupPermissionCompanyIds(userGroup: UnitNodeCodeTitleTypeCompanyId): string[] {
    //    if (this.getIsDisabled()) return [];
    //    if (!isNullOrUndefined(userGroup)) {
    //        const groupType = Permission.typeUserGroup;
    //        const groupCode = userGroup.code;
    //        //Get companyIds
    //        const currentPermission = this.explicitPermissions.find(j => j.type === groupType && j.code.ciEqual(groupCode));
    //        if (isNullOrUndefined(currentPermission)) return[];
    //        return currentPermission.companyIds.slice(0);
    //    }
    //    return [];
    //}

    getEffectiveUnitNodeCompanyIds(userGroup: IUnitPermissionUserGroup): EffectiveUnitNodeCompanyIds {
        const result = new EffectiveUnitNodeCompanyIds();
        result.isExplicitPermission = false;
        if (!this.explicitPermissions || this.explicitPermissions.length < 1) return result;
        const permissions = this.explicitPermissions.slice(0);
        //#2 User group permissions are relevant for all users
        //- Highest permissions considering all groups that user belongs to
        //- 'All group' permissions are used only if normal group permission does not exist
        if (userGroup) {
            let userGroupPermission: UnitNodePermission = null;
            let allUserGroupPermission: UnitNodePermission = null;
            let oneUserGroupPermission: UnitNodePermission = null;
            const relevantUserGroupPermissions = permissions.filter(k => EnumHelper.isEqual(k.type, GroupType.UserGroup));
            const userGroupCodes = userGroup.groupCodes.slice(0);
            userGroupCodes.push(userGroup.code);
            for (const permission of relevantUserGroupPermissions) {
                if (!permission.code) { //'All users' group
                    allUserGroupPermission = permission;
                } else {
                    for (const userGroupCode of userGroupCodes) {
                        if (!permission.code.equalIgnoreCase(userGroupCode)) continue;
                        if (EnumHelper.isEqual(permission.groupType, UserGroupType.Fixed)) { //One user user group
                            oneUserGroupPermission = permission;
                        } else { //Normal user groups
                            if (userGroupPermission === null || permission.getLevelTotal() > userGroupPermission.getLevelTotal()) {
                                userGroupPermission = permission;
                            }
                        }
                        break;
                    }
                }
            }
            if (oneUserGroupPermission !== null) {
                result.companyIds = oneUserGroupPermission.companyIds ? oneUserGroupPermission.companyIds.slice(0) : [];
                result.isExplicitPermission = oneUserGroupPermission.code === userGroup.code;
            } else if (userGroupPermission !== null) {
                result.companyIds = userGroupPermission.companyIds ? userGroupPermission.companyIds.slice(0) : [];
                result.isExplicitPermission = userGroupPermission.code === userGroup.code;
            } else if (allUserGroupPermission !== null) {
                result.companyIds = allUserGroupPermission.companyIds ? allUserGroupPermission.companyIds.slice(0) : [];
                result.isExplicitPermission = allUserGroupPermission.code === userGroup.code;
            }
        }
        return result;
    }
    //#endregion Permission CompanyIds
}